import { AsyncPipe, NgClass, NgFor, NgIf, NgStyle } from '@angular/common';
import { Component, DestroyRef, ElementRef, Input, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { VoiceSelectionDialogComponent } from '../../voice-selection-dialog/voice-selection-dialog.component';
import { Voice } from '../../../models/voice';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AiAgentResponse } from '../../../models/responses/ai-agent-response';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AiAgentService } from '../../../services/ai-agent.service';
import { UpdateAiAgentRequest } from '../../../models/requests/update-ai-agent-request';
import { AiConfigurationPageEventsService } from '../../../events/ai-configuration-page-events.service';
import { VoiceSettingsResponse } from '../../../models/responses/voice-settings-response';
import { AgentLanguageResponse } from '../../../models/responses/agent-language-reponse';
import ISO6391 from 'iso-639-1';
import { validLanguageVoiceProvider } from '../../../validators/voice-provider-language-validator';
import { UserPhoneNumbersDialogComponent } from '../../user-phone-numbers-page-components/user-phone-numbers-dialog/user-phone-numbers-dialog.component';
import { SubscriptionHelper } from '../../../classes/subscription-helper';
import { CONFIRMATION_DIALOG_TEXT, ConfirmationDialogComponent } from '../../general-dialogs-components/confirmation-dialog/confirmation-dialog.component';
import { PhoneSettingsService } from '../../../services/phone-settings.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AgentType } from '../../../models/enums/agent-type';
import { LatencyOption } from '../../../models/views/latency-option';
import { LatencyConfig } from '../../../models/enums/latency-config';
import { MatSliderModule } from '@angular/material/slider';
import { HangupSettings } from '../../../models/responses/hangup-settings-response';
import { ElevenLabsVoiceSettingsComponent } from '../integration-components/eleven-labs-voice-settings/eleven-labs-voice-settings.component';
import { LlmChoiceComponent } from '../integration-components/llm-choice/llm-choice.component';
import { LLMStyle } from '../../../models/enums/llm-style';
import { LlmChoice } from '../../../models/responses/llm-choice';
import { LLMProvider } from '../../../models/enums/llm-provider';
import { LlmChoiceService } from '../../../services/llm-choice.service';



@Component({
  selector: 'app-ai-agent-configuration',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    NgIf,
    NgClass,
    NgStyle,
    AsyncPipe,
    MatTabsModule,
    MatSliderModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDivider,
    ReactiveFormsModule,
    NgFor,
    NgIf,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    ElevenLabsVoiceSettingsComponent,
    LlmChoiceComponent
  ],
  templateUrl: './ai-agent-configuration.component.html',
  styleUrl: './ai-agent-configuration.component.scss'
})
export class AiAgentConfigurationComponent {
  languages: { language: string, name: string, aiAgentId?: number, id?: number, routingDigit?: number }[] = [
    { language: "bg", name: this.getLanguageName("bg") },
    { language: "ca", name: this.getLanguageName("ca") },
    { language: "zh", name: this.getLanguageName("zh") },
    { language: "cs", name: this.getLanguageName("cs") },
    { language: "da", name: this.getLanguageName("da") },
    { language: "nl", name: this.getLanguageName("nl") },
    { language: "en", name: this.getLanguageName("en") },
    { language: "et", name: this.getLanguageName("et") },
    { language: "fi", name: this.getLanguageName("fi") },
    { language: "fr", name: this.getLanguageName("fr") },
    { language: "de", name: this.getLanguageName("de") },
    { language: "el", name: this.getLanguageName("el") },
    { language: "hi", name: this.getLanguageName("hi") },
    { language: "hu", name: this.getLanguageName("hu") },
    { language: "id", name: this.getLanguageName("id") },
    { language: "it", name: this.getLanguageName("it") },
    { language: "ja", name: this.getLanguageName("ja") },
    { language: "ko", name: this.getLanguageName("ko") },
    { language: "lv", name: this.getLanguageName("lv") },
    { language: "lt", name: this.getLanguageName("lt") },
    { language: "ms", name: this.getLanguageName("ms") },
    { language: "no", name: this.getLanguageName("no") },
    { language: "pl", name: this.getLanguageName("pl") },
    { language: "pt", name: this.getLanguageName("pt") },
    { language: "ro", name: this.getLanguageName("ro") },
    { language: "ru", name: this.getLanguageName("ru") },
    { language: "sk", name: this.getLanguageName("sk") },
    { language: "es", name: this.getLanguageName("es") },
    { language: "sv", name: this.getLanguageName("sv") },
    { language: "th", name: this.getLanguageName("th") },
    { language: "tr", name: this.getLanguageName("tr") },
    { language: "uk", name: this.getLanguageName("uk") },
    { language: "vi", name: this.getLanguageName("vi") },
    { language: "multi", name: "Multilingual" },
    { language: "fr-ca", name: "Canadian French" }
  ];
  latencyOptions: LatencyOption[] = [
    { latencyConfig: LatencyConfig.LOW, latencyDescription: '≈ 1.5 sec', name: 'Low', selected: false },
    { latencyConfig: LatencyConfig.MEDIUM, latencyDescription: '≈ 2.5 sec', name: 'Medium', selected: false },
    { latencyConfig: LatencyConfig.HIGH, latencyDescription: '≈ 3.5 sec', name: 'High', selected: false },
  ]
  selectedLanguages: AgentLanguageResponse[] = []
  private selectedLatencyConfig!: LatencyConfig;
  phoneNumber: FormControl = new FormControl();
  @Input() aiAgent!: AiAgentResponse;
  isPhoneNumberLoading: boolean = false;
  isAssistantProfileFormLoading: boolean = false;
  assistantProfileForm!: FormGroup;
  voiceSample!: string;
  isVoicePlaying: boolean = false;
  snackBarDuration = 5000;
  fileSet: boolean = false;
  file_store!: FileList;
  file_list: Array<string> = [];
  subscriptions: SubscriptionHelper;
  @ViewChild('audio') audioPlayerRef!: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private aiAgentService: AiAgentService,
    private snackBar: MatSnackBar,
    private aiConfigurationPageEventService: AiConfigurationPageEventsService,
    private destroyRef: DestroyRef,
    private phoneSetttingsService: PhoneSettingsService,
    private llmChoiceService: LlmChoiceService

  ) {
    this.subscriptions = new SubscriptionHelper(destroyRef);
  }

  ngAfterViewInit() {
    this.addEventListenerToAudio();
  }

  ngOnInit() {
    this.setChosenLatency(this.aiAgent.latencySettings.latencyConfig);
    this.setVoiceSample(this.aiAgent.voiceSettings);
    this.initLanguages();
    this.subscribeToOnPhoneNumberSelected();
    this.initFormGroup();
    this.initAiAgentPhoneNumberForm()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['aiAgent'].currentValue) {
      this.initFormGroup();
      this.initAiAgentPhoneNumberForm();
    }
  }

  // FORM SERVICES

  initFormGroup() {
    this.assistantProfileForm = this.formBuilder.group({
      name: [this.aiAgent.name, Validators.required],
      agentContext: [this.aiAgent.agentContext],
      agentLanguages: [this.aiAgent.agentType == AgentType.OUTBOUND ? this.selectedLanguages[0] : this.selectedLanguages, Validators.required],
      chosenVoiceId: [this.aiAgent.voiceSettings.chosenVoiceId, Validators.required],
      chosenVoiceName: [this.aiAgent.voiceSettings.chosenVoiceName, Validators.required],
      voiceProvider: [this.aiAgent.voiceSettings.voiceProvider, Validators.required],
      voiceSpeed: [this.aiAgent.voiceSettings.speed, Validators.required],
      llmStyle: [this.aiAgent.llmChoice?.style || LLMStyle.REGULAR],
      voiceStability: [this.aiAgent.voiceSettings.stability, Validators.required],
      voiceSimilarity: [this.aiAgent.voiceSettings.similiarity, Validators.required],
      isNonInterruptibleMessage: [this.aiAgent.isNonInterruptibleMessage, Validators.required],
      hangupAfterGreet: [this.aiAgent.hangupSettings?.hangupAfterGreet || false, Validators.required],
      sample: [this.aiAgent.voiceSettings.sample],
      greeting: [this.aiAgent.greeting, [Validators.required, Validators.minLength(5)]],
    });

    this.assistantProfileForm.setValidators(validLanguageVoiceProvider("agentLanguages", "chosenVoiceName"));
  }


  // PHONE NUMBER SERVICES
  subscribeToOnPhoneNumberSelected() {
    this.subscriptions.add(this.aiConfigurationPageEventService.onSelectPhoneNumberEvent.subscribe(phoneSettings => {
      this.aiAgent.phoneSettings = phoneSettings;
      this.initAiAgentPhoneNumberForm();
    }));
  }

  onRemoveClicked() {
    this.dialog.open(ConfirmationDialogComponent, {
      autoFocus: false,
      width: '25%',
      data: { title: 'Remove phone', data: 'Are you sure you want to unassign the current phone number from the assistant?' }
    }).afterClosed().subscribe(result => {
      if (result == CONFIRMATION_DIALOG_TEXT) {
        this.removePhoneNumberFromAiAgent();
      }
    });
  }

  removePhoneNumberFromAiAgent() {
    if (this.aiAgent.phoneSettings && this.aiAgent.phoneSettings.id) {
      this.isPhoneNumberLoading = true;

      this.phoneSetttingsService.removePhoneNumberFromAiAgent(this.aiAgent.phoneSettings.id, this.aiAgent.id).subscribe({
        next: () => {
          this.isPhoneNumberLoading = false;
          this.aiAgent.phoneSettings = undefined;
          this.initAiAgentPhoneNumberForm();
          this.openSnackBar('Phone number removed successfully');
        },
        error: () => {
          this.isPhoneNumberLoading = false;
          alert('Server error contact support');
        }
      });
    }
  }

  onSelectClicked() {
    this.dialog.open(UserPhoneNumbersDialogComponent, {
      width: '80%',
      autoFocus: false,
      data: this.aiAgent
    });
  }

  // ASSISTANT PROFILE SERVICES

  openVoiceSelectionDialog() {
    const dialogRef = this.dialog.open(VoiceSelectionDialogComponent, {
      width: '80%'
    });

    dialogRef.afterClosed().subscribe((voice: Voice) => {
      if (voice) {
        this.voiceSample = voice.sample;
        this.assistantProfileForm.controls['chosenVoiceId']!.setValue(voice.id);
        this.assistantProfileForm.controls['chosenVoiceName']!.setValue(voice.name);
        this.assistantProfileForm.controls['voiceProvider']!.setValue(voice.voiceProvider);
        this.assistantProfileForm.controls['sample']!.setValue(voice.sample);
        this.assistantProfileForm.controls['chosenVoiceName']!.markAsTouched();
      }
    });
  }

  playVoice() {
    if (this.isVoicePlaying) {
      this.isVoicePlaying = false;
      this.audioPlayerRef.nativeElement.pause();
    } else {
      this.audioPlayerRef.nativeElement.src = this.voiceSample;
      this.audioPlayerRef.nativeElement.load();
      this.audioPlayerRef.nativeElement.play();
      this.isVoicePlaying = true;
    }
  }

  addEventListenerToAudio() {
    this.audioPlayerRef.nativeElement.addEventListener('ended', () => {
      this.isVoicePlaying = false;
    });
  }

  initAiAgentPhoneNumberForm() {
    if (this.aiAgent.phoneSettings) {
      this.phoneNumber.setValue(this.aiAgent.phoneSettings.phoneNumber);
    } else {
      this.phoneNumber.setValue('');
    }
  }

  updateAssistantProfile() {
    if (this.assistantProfileForm.valid) {
      const formValue = this.assistantProfileForm.value;

      this.isAssistantProfileFormLoading = true;
      this.updateLLMChoice(formValue);
      
      let agentLatencySettings = this.aiAgent.latencySettings;
      if (agentLatencySettings) {
        agentLatencySettings.latencyConfig = this.selectedLatencyConfig;
      }
      let hangUpSettings = this.aiAgent.hangupSettings || { id: -1 } as HangupSettings;

      if (hangUpSettings) {
        hangUpSettings.hangupAfterGreet = formValue['hangupAfterGreet']
      }
      
      let selectedLanguageValue = formValue['agentLanguages'];
      const selectedLanguages: AgentLanguageResponse[] = Array.isArray(selectedLanguageValue) ? selectedLanguageValue : [selectedLanguageValue];

      const formData = this.getFileData();

      let updateAiAgentRequest: UpdateAiAgentRequest = {
        name: formValue['name'],
        chosenVoiceId: formValue['chosenVoiceId'],
        voiceProvider: formValue['voiceProvider'],
        chosenVoiceName: formValue['chosenVoiceName'],
        voiceSpeed: formValue['voiceSpeed'],
        voiceSimilarity: formValue['voiceSimilarity'],
        voiceStability: formValue['voiceStability'],
        sample: formValue['sample'],
        isNonInterruptibleMessage: formValue['isNonInterruptibleMessage'],
        agentLanguages: selectedLanguages,
        latencySettings: agentLatencySettings,
        agentType: this.aiAgent.agentType,
        greeting: formValue['greeting'],
        agentContext: formValue['agentContext'],
        hangupSettings: hangUpSettings,
        id: this.aiAgent.id
      };

      updateAiAgentRequest.agentLanguages = updateAiAgentRequest.agentLanguages.map(l => {
        return {
          id: l.id,
          language: l.language,
          routingDigit: l.routingDigit,
          aiAgentId: l.aiAgentId
        }
      });
      this.aiAgentService.updateAiAgent(updateAiAgentRequest).subscribe({
        next: (aiAgent) => {
          this.aiAgent = aiAgent;
          if (formData) {
            this.uploadIntroMessage(formData);
          } else {
            this.isAssistantProfileFormLoading = false;
            this.aiAgent = aiAgent;
            this.openSnackBar('Profile was updated successfully');
          }
        },
        error: () => {
          alert('Server error contact support');
          this.isAssistantProfileFormLoading = false;
        }
      });
    }
  }

  onDeleteIntroMessage() {
    this.aiAgentService.deleteIntroMessage(this.aiAgent.id).subscribe({
      next: (aiAgent) => {
        this.isAssistantProfileFormLoading = false;
        this.aiAgent = aiAgent;
        this.openSnackBar('Intro message was deleted successfully');
      },
      error: () => {
        alert('Server error contact support');
        this.isAssistantProfileFormLoading = false;
      }
    });
  }

  handleFileInputChange(l: FileList) {
    this.file_store = l;
    this.fileSet = this.file_store.length > 0;
  }

  setVoiceSample(voiceSettings: VoiceSettingsResponse) {
    this.voiceSample = voiceSettings.sample;
  }

  getLanguageName(code: string) {
    return ISO6391.getName(code);
  }

  setChosenLatency(latency: LatencyConfig) {
    this.latencyOptions.forEach(x => x.selected = false);
    let latencyOption = this.latencyOptions.find(x => x.latencyConfig == latency);
    if (latencyOption) {
      latencyOption.selected = true;
      this.selectedLatencyConfig = latency
      this.selectedLatencyConfig = latency;
    }

  }

  // PRIVATE METHODS

  private openSnackBar(message: string) {
    this.snackBar.open(message, 'close', {
      duration: this.snackBarDuration
    });
  }

  private getFileData() {
    if (this.file_store && this.file_store.length > 0) {
      let fd = new FormData();
      fd.append("file", this.file_store[0], this.file_store[0].name);
      return fd;
    }

    return undefined;
  }

  private uploadIntroMessage(formData: FormData) {
    this.aiAgentService.createIntroMessage(this.aiAgent.id, formData).subscribe({
      next: (aiAgent) => {
        this.isAssistantProfileFormLoading = false;
        this.aiAgent = aiAgent;
        this.openSnackBar('Profile was updated successfully');
      },
      error: () => {
        alert('Server error contact support');
        this.isAssistantProfileFormLoading = false;
      }
    });
  }

  private updateLLMChoice(formValue: any) {
    const llmStyle = formValue['llmStyle'] as LLMStyle;

    if (this.aiAgent.llmChoice) {
      const llmChoice = this.aiAgent.llmChoice;
      llmChoice.style = llmStyle;
      this.llmChoiceService.update(llmChoice).subscribe({
        next: (llmChoice) => {
          this.aiAgent.llmChoice = llmChoice;
        },
        error: () => {
          alert('Server error contact support');
        }
      })

    } else {
      const llmChoice: LlmChoice = { aiAgentId:this.aiAgent.id, style: llmStyle, provider: LLMProvider.OPENAI }
      this.llmChoiceService.create(llmChoice).subscribe({
        next: (llmChoice) => {
          this.aiAgent.llmChoice = llmChoice;
        },
        error: () => {
          alert('Server error contact support');
        }
      })
    }
  }

  private initLanguages() {
    for (let lang of this.languages) {
      const match = this.aiAgent.agentLanguages.find(x => x.language == lang.language);
      if (match) {
        lang.aiAgentId = match.aiAgentId;
        lang.id = match.id;
        lang.language = match.language;
        lang.routingDigit = match.routingDigit;
        this.selectedLanguages.push(lang);
      }
    }
  }
}